import { Injectable, NgZone } from '@angular/core';
import { AuthService } from '@app/modules/auth';
import { MDLogin, MDLoginResponse, LoginResponseStatus, MDLoginType, Request, StrikePriceRequest, StrikePriceResponse, DeltaRequest, DeltaResponse } from '../../../../../utProto/marketData_pb';
import { DeSerializedMessage, subscribeQuoteParams, SocketMessageI, LegSymbol, LTPAndClosePiceObj } from '../websocket.interfaces';
import { Observable, Subject } from 'rxjs';
import { QUOTE_UPDATE_TYPE, SYMBOL_SUBSCRIPTION_TYPE, SOCKET_MSG_TYPES, FirstFetchDataType, SocketType, WS_APP_CLOSE_CODES } from '../websocket.enums';
import { ConfigurationService } from '@config/configuration.service';
import { Utils } from '@app/_helpers/utilities';
import { MAX_DEPTH_NULL_VALUE, MIN_DEPTH_NULL_VALUE } from '@app/_helpers/constants';
import { Quote, QuoteStoreService } from '../../quote-store';
import { MarketIDMap } from '@app/_shared/shared.enums';
import { SymbolDetailsService } from '../../symbol-details/symbol-details.service';
import { SymbolSearchService } from '../../symbol-search';
import { OhlcDataResponse } from '../../../../../utProto/chartingData_pb';
import { StrikeSelectionType } from '../../../../../utProto/webApiStrategyEnums_pb';
import { MixpanelService } from '../../mixpanel/mixpanel.service';
import { BaseSocket } from '../base.socket';

@Injectable({
    providedIn: 'root'
})
export class MarketSocketService extends BaseSocket {

    private quoteUpdateSubject = new Map<QUOTE_UPDATE_TYPE, Subject<any>>();
    isReconnection = false;
    subscribedSymbols: Map<string, {}> = new Map();
    dataSentToSocket = []; // the array will be having data that is sent to socket.
    /**
     * UT-8068: Ref-counted subscriptions can be torn down by transient consumers (option chain,
     * strategy forms) while the always-mounted index ticker still needs live updates.
     * Pin count prevents sendUnsubscribeSymbol when other components drop their ref to zero.
     */
    private pinnedSymbols = new Map<string, number>();
    /** Emitted after a socket reconnect so long-lived consumers (index ticker) can re-subscribe. */
    reconnected$ = new Subject<void>();

    strikePricesMap: Map<string, number> = new Map();
    strikePriceObjectMap: Map<number, string> = new Map();
    strikePriceRequestResponseMap: Map<number, LegSymbol> = new Map();

    /**
     * subscribedSymbolMap is used for unsubscription case. Whenever a symbol is subscribed storing the data that is being sent,
     * So while unsubscribing, marketId + securityId is used to fetch the data from this map. This map helps in avoiding to send QuoteParams
     * from all places where unsubcription is called.
     */
    public subscribedSymbolMap = new Map<string, subscribeQuoteParams>();

    constructor(
        authProvider: AuthService,
        confService: ConfigurationService,
        private _quoteStore: QuoteStoreService,
        private symbolsDetailsService: SymbolDetailsService,
        private symbolSearchService: SymbolSearchService,
        mixpanel: MixpanelService,
        private _ngZone: NgZone
    ) {
        super(authProvider, mixpanel, confService, SocketType.MARKET_DATA_SOCKET, _ngZone);
        this.socketUrl = Utils.changeUrlToWss(Utils.sanitizeUrl(confService.MARKET_SOCKET_HOST));
        this.binarySocket = true;
        this.RETRY_DELAY_MS = 2000;
    }

    protected onRetry(): void {
        this.isReconnection = true;
    }

    messageDeSerializer(msg: any): DeSerializedMessage {
        const uintArr = new Uint8Array(msg);
        const msgType = Utils.ntohl(uintArr, 4);
        return {
            messageType: msgType,
            message: msg
        };
    }

    sendStrikePriceRequest(data: LegSymbol, pricerequestId?: number) {
        const key = Utils.getJoinedString(data.exchange, data.symbol, data.optionMode,
            data.expiryType, data.expiryOffset, data.expiryDate, data.strikeValue, data.strikeSelection, data.underlyingType);

        /**
         * sending strike price request to mdl only if strike price is not present at our end
         */
        if (!this.strikePricesObj(key) && (data.strikeValue >= 0)) {
            const requestId = pricerequestId ? pricerequestId : Date.now();
            this.strikePriceObjectMap.set(requestId, key);
            const commandCategory = Utils.htonl(Request.REQUEST_STRIKE_PRICE_SELECTION);
 
            const req = new StrikePriceRequest();
            req.setRequestId(requestId)
            req.setExchangeId(data.exchange);
            req.setExpiryDate(data.expiryDate);
            req.setStrikePrice(data.strikeValue);
            req.setOptionMode(data.optionMode);
            req.setSymbolName(data.symbol);
            req.setExpiryType(data.expiryType);
            req.setExpiryOffset(data.expiryOffset);
            req.setStrikeSelection(data.strikeSelection);
            req.setInstrumentType(data.instrumentType);
            req.setTimestamp(data.timestamp);
            req.setUnderlyingType(data.underlyingType);
            req.setCandleInterval(1);
            req.setItmDepth(data.itmDepth);
            req.setOtmDepth(data.otmDepth);
            req.setMinPriceRange(data.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_RANGE ? data.minPriceRange : MIN_DEPTH_NULL_VALUE);
            req.setMaxPriceRange(data.strikeSelection === StrikeSelectionType.STRIKESELECTIONTYPE_PRICE_RANGE ? data.maxPriceRange : MAX_DEPTH_NULL_VALUE);
            req.setGroupName("");
            req.setStrikeOperator(2);

            // Converting to typed array
            const strikePriceProto = req.serializeBinary();

            // Creating Command Category
            const commandCategoryBuff = new Uint8Array(commandCategory);
            const strikePriceReq = new Uint8Array(commandCategoryBuff.byteLength + commandCategoryBuff.byteLength + strikePriceProto.length);

            /** Setting data for binary request
             0-4 Total Message Length
             4-8 Message type
             8-* Message body
             */
            strikePriceReq.set(new Uint8Array(Utils.htonl(commandCategoryBuff.byteLength + strikePriceProto.length)), 0);
            strikePriceReq.set(commandCategoryBuff, commandCategoryBuff.byteLength);
            strikePriceReq.set(strikePriceProto, 8);
            if (!pricerequestId) {
                this.strikePriceRequestResponseMap.set(requestId, data)
            }
            if (this.connection$ && !this.connection$.closed && this.authenticatedSocket) {
                this.connection$.next(strikePriceReq);
            }
        }
    }

    sendHeartbeat(): void {
        const reqBuffer = new ArrayBuffer(8);
        const subMsg = new DataView(reqBuffer);
        subMsg.setUint32(0, 4);
        subMsg.setUint32(4, Request.REQUEST_HEARTBEAT);
        if (!this.connection$ || this.connection$.closed) return;
        try {
            this.connection$.next(subMsg);
        } catch (_) {
            this.closeSocketStream(WS_APP_CLOSE_CODES.HEARTBEAT_FAILED, 'heartbeat_request_failed');
        }
    }

    sendAuthMessage(): void {
        const commandCategory = Utils.htonl(Request.REQUEST_LOGIN);
        // Creating request obj
        const req = new MDLogin();

        // Setting Params
        req.setLoginType(MDLoginType.MDLOGINTYPE_JWT_TOKEN);
        req.setToken(this.authProvider.userInfo.access);

        // Converting to typed array
        const loginProtoBy = req.serializeBinary();

        // Creating Command Category
        const commandCategoryBuff = new Uint8Array(commandCategory);

        // Creating binary login request sequence
        // 4 bytes + 4 bytes + len(body) in bytes
        const loginReq = new Uint8Array(commandCategoryBuff.byteLength + commandCategoryBuff.byteLength + loginProtoBy.length);

        /** Setting data for binary request
         0-4 Total Message Length
         4-8 Message type
         8-* Message body
         */
        loginReq.set(new Uint8Array(Utils.htonl(commandCategoryBuff.byteLength + loginProtoBy.length)), 0);
        loginReq.set(commandCategoryBuff, commandCategoryBuff.byteLength);
        loginReq.set(loginProtoBy, 8);
        this.connection$.next(loginReq);
    }


    protected afterOnAuthenticated(): void {
        if (!this.isReconnection) return;
        if (this.dataSentToSocket.length) {
            this.dataSentToSocket.forEach(el => {
                const reqBuffer = new ArrayBuffer(20);
                const subMsg = new DataView(reqBuffer);
                subMsg.setUint32(0, 20);
                subMsg.setUint32(4, el.command);
                subMsg.setUint8(8, el.subscriptionType);
                subMsg.setInt16(9, el.exchangeId);
                subMsg.setUint32(11, el.securityId);
                subMsg.setUint8(15, el.msgType);
                subMsg.setUint32(16, 1);
                this.send(subMsg);
            });
        }
        this.resendStrikeRequest();
        // Notify consumers that replayed subscriptions may be incomplete; allow them to re-subscribe.
        this.reconnected$.next();
        this.isReconnection = false;
    }

    /**
     * below function is used to resend strikeprice request again to mdl
     * in two cases: if suppose mdl is disconnected or if response has been
     * not received from mdl.
     */
    resendStrikeRequest() {
        if (this.strikePriceRequestResponseMap.size) {
            for (const [key, value] of this.strikePriceRequestResponseMap) {
                this.sendStrikePriceRequest(value, key);
            }
        }
    }

    tapMessage(msg: DeSerializedMessage, index: number = 0) {
        try {
            switch (msg.messageType) {
                case SOCKET_MSG_TYPES.Login_Response:
                    const authResponse = MDLoginResponse.deserializeBinary(msg.message);
                    if (authResponse.getLoginStatus() === LoginResponseStatus.LOGINRESPONSESTATUS_SUCCESS) {
                        this.onAuthenticated();
                    } else {
                        this.handleAuthFailure();
                    }
                    break;
                case SOCKET_MSG_TYPES.FIRST_FETCH:
                    const socketData = new DataView(msg.message);
                    const msgLen = socketData.getUint32(0);
                    const msgType = socketData.getUint32(4);
                    const symbolId = Utils.getUint64(socketData, 8, false);
                    const remData = msg.message.slice(16);
                    const remDataView = new DataView(remData);
                    let startIndex = 0;
                    while(startIndex<remDataView.byteLength){
                        // Two message length coming from MDL in first first
                        // skipping first message length.
                        startIndex+=4;
                        let currentMsgType = remDataView.getUint32(startIndex+4);
                        this.tapMessage({message: remData, messageType: currentMsgType}, startIndex);
                        // Reading first message length to skip bytes to read next message.
                        startIndex+=remDataView.getUint32(startIndex-4);
                    }
                    break;

                case SOCKET_MSG_TYPES.QUOTE:
                    this._quoteStore.processQuoteUpdate(msg.message, index);
                    break;

                
                case SOCKET_MSG_TYPES.DAILY_PRICE_RANGE:
                    this._quoteStore.processDPR(msg.message, index);
                    break;

                case SOCKET_MSG_TYPES.TOP_DEPTH_QUOTE:
                    this._quoteStore.processQuoteUpdate(msg.message, index);
                    break;

                case SOCKET_MSG_TYPES.INDEX_VALUE:
                    this._quoteStore.processIndexQuoteUpdate(msg.message, index);
                    break;

                case SOCKET_MSG_TYPES.OPEN_INTEREST:
                    this._quoteStore.processOIUpdates(msg.message, index);
                    break;

                case SOCKET_MSG_TYPES.LTP_Close_PRICE:
                    this._quoteStore.processLtpAndClose(msg.message, index);
                    break;

                case SOCKET_MSG_TYPES.FIFTY_DEPTH:
                    this._quoteStore.process50DepthUpdate(msg.message, index);
                    break;
                
                case SOCKET_MSG_TYPES.GROUP_QUOTE:
                    this._quoteStore.processGroupQuote(msg.message);
                    break;

                case SOCKET_MSG_TYPES.STRIKE_PRICE:
                    const strikePriceResponse = StrikePriceResponse.deserializeBinary(msg.message);
                    const key = this.strikePriceObjectMap.get(strikePriceResponse.getResponseid());
                    /**
                     * if response has been recieved from mdl then deleting the key from pending response map
                     */
                    if (this.strikePriceRequestResponseMap.has(strikePriceResponse.getResponseid()) && strikePriceResponse.getActualprice()) {
                        this.strikePriceRequestResponseMap.delete(strikePriceResponse.getResponseid());
                    }

                    this.strikePricesMap.set(key, (strikePriceResponse.getActualprice() / 100));
                    this.strikePriceObjectMap.delete(strikePriceResponse.getResponseid());
                    

                    this.getFeedSubject(QUOTE_UPDATE_TYPE.StrikePrice).next(key);


                case SOCKET_MSG_TYPES.OHLC_DATA:
                    const response = OhlcDataResponse.deserializeBinary(msg.message.slice(4));
                    this._quoteStore.processOHLC(response);
                    break;

                case SOCKET_MSG_TYPES.DELTA:
                    const deltaResponse = DeltaResponse.deserializeBinary(msg.message);
                    this._quoteStore.processDeltaResponse(deltaResponse);
                    break;

                default:
                    break;
            }
        } catch (error) {
            this.trackSocketError(msg?.messageType ?? null, error);
            console.error('Error in tapping message', error);
        }
    }

    subscribeSymbol(
        symbolData: subscribeQuoteParams,
        msgType = SOCKET_MSG_TYPES.LTP_Close_PRICE,
    ) {
        // setting symbol precision in symbol precision map
        if(!this._quoteStore.symbolPrecMap.has(Utils.getJoinedString(symbolData.market_id, symbolData.security_id))) {
            this._quoteStore.symbolPrecMap.set(Utils.getJoinedString(symbolData.market_id, symbolData.security_id), { symbolId: symbolData.symbol_id, scripPrecision: symbolData.scrip_precision });
        }
        if (!Object.values(symbolData).includes(NaN)) {
            if (!this.subscribedSymbolMap.has(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id))) {
                this.subscribedSymbolMap.set(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id), symbolData);
            }

            if (this.subscribedSymbols.has(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id))) {
                const keys = Object.keys(this.subscribedSymbols.get(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id)));
                /**
                 * converting msgType to string because Object.keys() returns an array whose elements are strings
                 * if symbol is already subscribed first fetch data is not required again. So in else
                 * case subscriptiontype is sent as subscribed.
                 */
                if (keys.includes(msgType.toString())) {
                    this.subscribedSymbols.get(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id))[msgType]++;
                } else {
                    this.subscribedSymbols.get(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id))[msgType] = 1;
                    this.sendSubscribeSymbol(symbolData, msgType, SYMBOL_SUBSCRIPTION_TYPE.SUBSCRIBED);
                }
            } else {
                this.subscribedSymbols.set(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id), {});
                this.subscribedSymbols.get(this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id))[msgType] = 1;
                this.sendSubscribeSymbol(symbolData, msgType, SYMBOL_SUBSCRIPTION_TYPE.FIRST_FETCH_SUBSCRIPTION);
            }
        }
    }

    sendSubscribeSymbol(symbolData: subscribeQuoteParams, msgType: SOCKET_MSG_TYPES, subscriptiontype: SYMBOL_SUBSCRIPTION_TYPE) {
        /**
         * msgLen + mstype + subscriptiontype+ exchangeId + securityid  + msgtype
         * 4+ 4+ 1 + 2 + 4 + 1 = 16(bytes)
         */
        const reqBuffer = new ArrayBuffer(20);
        const subMsg = new DataView(reqBuffer);
        subMsg.setUint32(0, 20);
        subMsg.setUint32(4, Request.REQUEST_SUBSCRIPTION);
        subMsg.setUint8(8, subscriptiontype);
        subMsg.setInt16(9, symbolData.market_id);
        subMsg.setUint32(11, symbolData.security_id);
        subMsg.setUint8(15, msgType);
        subMsg.setUint32(16, 1);
        this.dataSentToSocket.push({
            command: Request.REQUEST_SUBSCRIPTION,
            subscriptionType: subscriptiontype,
            exchangeId: symbolData.market_id,
            securityId: symbolData.security_id,
            msgType
        });
        this.send(subMsg);
    }


    /** UT-8068: Mark a symbol subscription as pinned so unSubscribeSymbol cannot tear it down. */
    pinSymbolSubscription(symbolData: subscribeQuoteParams, msgType = SOCKET_MSG_TYPES.LTP_Close_PRICE) {
        const pinKey = this.getPinKey(symbolData, msgType);
        this.pinnedSymbols.set(pinKey, (this.pinnedSymbols.get(pinKey) || 0) + 1);
    }

    /** UT-8068: Release a pin; server unsub is only sent once both pin and ref counts reach zero. */
    unpinSymbolSubscription(symbolData: subscribeQuoteParams, msgType = SOCKET_MSG_TYPES.LTP_Close_PRICE) {
        const pinKey = this.getPinKey(symbolData, msgType);
        const count = this.pinnedSymbols.get(pinKey) || 0;
        if (count <= 1) {
            this.pinnedSymbols.delete(pinKey);
        } else {
            this.pinnedSymbols.set(pinKey, count - 1);
        }
    }

    private getPinKey(symbolData: subscribeQuoteParams, msgType: SOCKET_MSG_TYPES): string {
        return `${this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id)}_${msgType}`;
    }

    unSubscribeSymbol(symbolData: subscribeQuoteParams, msgType = SOCKET_MSG_TYPES.LTP_Close_PRICE) {
        const mapKey = this._quoteStore.getMapKey(symbolData.market_id, symbolData.security_id);
        if (this.subscribedSymbols.has(mapKey)) {
            const keys = Object.keys(this.subscribedSymbols.get(mapKey));
            /**
             * converting msgType to string because Object.keys() returns an array whose elements are strings
             */
            if (keys.includes(msgType.toString())) {
                this.subscribedSymbols.get(mapKey)[msgType]--;
                if (!this.subscribedSymbols.get(mapKey)[msgType]) {
                    const pinCount = this.pinnedSymbols.get(this.getPinKey(symbolData, msgType)) || 0;
                    // UT-8068: Keep subscription alive on server while index ticker (or other pinner) holds a pin.
                    if (pinCount > 0) {
                        this.subscribedSymbols.get(mapKey)[msgType] = 1;
                        return;
                    }
                    /**
                     * if there is no subscription for particular type for a symbol, removing that type from subscriptionmap.
                     */
                    delete this.subscribedSymbols.get(mapKey)[msgType];
                    if (Object.keys(this.subscribedSymbols.get(mapKey)).length === 0) {
                        /**
                         * if there is no subscription for particular symbol, removing that symbol from subscriptionmaps.
                         */
                        this.subscribedSymbols.delete(mapKey);
                        this.subscribedSymbolMap.delete(mapKey);
                    }
                    this.sendUnsubscribeSymbol(symbolData, msgType);

                }
            }
        }
    }

    unSubscribeFeed(symbolIds: string[], type = SOCKET_MSG_TYPES.TOP_DEPTH_QUOTE) {
        for (let i = 0; i < symbolIds.length; i++) {
            if (this.subscribedSymbolMap.has(symbolIds[i])) {
                this.unSubscribeSymbol(this.subscribedSymbolMap.get(symbolIds[i]), type);
            }
        }
    }

    sendUnsubscribeSymbol(symbolData: subscribeQuoteParams, msgType: SOCKET_MSG_TYPES) {
        /**
         * msgLen + mstype + subscriptiontype+ exchangeId + securityid  + msgtype
         * 4+ 4+ 1 + 2 + 4 + 1 = 16(bytes)
         */
        const subMsg: SocketMessageI = {
            command: Request.REQUEST_SUBSCRIPTION,
            subscriptionType: SYMBOL_SUBSCRIPTION_TYPE.UNSUBSCRIBED,
            exchangeId: symbolData.market_id,
            securityId: symbolData.security_id,
            msgType
        };
        this.dataSentToSocket = this.dataSentToSocket.filter(el => !((this._quoteStore.getMapKey(el.exchangeId, el.securityId) ===
            this._quoteStore.getMapKey(subMsg.exchangeId, subMsg.securityId)) && (el.msgType === subMsg.msgType)));

        const unsubscribeRequest = new ArrayBuffer(16);
        const unSubMsg = new DataView(unsubscribeRequest);
        unSubMsg.setUint32(0, 16);
        unSubMsg.setUint32(4, Request.REQUEST_SUBSCRIPTION);
        unSubMsg.setUint8(8, SYMBOL_SUBSCRIPTION_TYPE.UNSUBSCRIBED);
        unSubMsg.setInt16(9, symbolData.market_id);
        unSubMsg.setUint32(11, symbolData.security_id);
        unSubMsg.setUint8(15, msgType);
        this.send(unSubMsg);
    }


    getFeedSubject<T = any>(type: QUOTE_UPDATE_TYPE) {
        if (!this.quoteUpdateSubject.has(type)) {
            this.quoteUpdateSubject.set(type, new Subject<T>());
        }
        return this.quoteUpdateSubject.get(type) as Subject<T>;
    }



    /**
     * below function is used to get strike price against the
     * price selected by user. here if already the price is present in the map
     * then directly returning from map other wise sending request to mdl.
     */
    getStrikePrice(req: LegSymbol, key: string) {
        return new Promise((resolve) => {
            if (this.strikePricesMap.has(key)) {
                resolve(this.strikePricesMap.get(key));
            }
            else {
                this.sendStrikePriceRequest(req);

                const sub = this.getFeedSubject(QUOTE_UPDATE_TYPE.StrikePrice).subscribe(reskey => {
                    if (key === reskey) {
                        sub.unsubscribe();
                        clearInterval(timeInterval);
                        resolve(this.strikePricesMap.get(key));

                    }
                })
                /**
                 * setting requestcounter to 2 and after 3 sec
                 * if response and not recieved then sending request again
                 * and decrementing the counter. again after 3 sec if response
                 * as not been recieved directly resolving it to 0 and clearing timeinterval
                 */
                let requestCounter = 2;
                const timeInterval = setInterval(() => {
                    requestCounter--;
                    if (!requestCounter) {
                        sub.unsubscribe();
                        clearInterval(timeInterval);
                        resolve(0);
                    }
                    else {
                        this.sendStrikePriceRequest(req);
                    }

                }, 3000)
            }
        });
    }

    getSingleUpdatedQuote(market_id: string, security_id: number, update = false) {
        return new Promise((resolve) => {
            //RA-2183
            //Check if quote is available if available also check LTP is set
            if (!update && this._quoteStore.quotes.has(this._quoteStore.getMapKey(MarketIDMap[market_id], security_id))
                && this._quoteStore.quotes.get(this._quoteStore.getMapKey(MarketIDMap[market_id], security_id)).LTP > 0
            ) {
                resolve(Object.assign({}, this._quoteStore.quotes.get(this._quoteStore.getMapKey(MarketIDMap[market_id], security_id))));
            } else {
                let quoteObj = this._quoteStore.getFeedObj(MarketIDMap[market_id], security_id);
                this.subscribeSymbol({ market_id: MarketIDMap[market_id], security_id: security_id });

                // RA-2084 :
                // wait for 1000ms if quote update is not received until timer expires
                // resolve promise with default values of quote object
                const timer = setTimeout(() => {
                    clearTimeout(timer);

                    const res = this.fetchClosePrice([[market_id, security_id]]);
                    quoteObj = this.updateQuotesMap(res)[0];
                    resolve(Object.assign({}, quoteObj))

                }, 1000)
                const subs = quoteObj.ltpUpdate.subscribe(_ => {
                    if (quoteObj.LTP > 0) {
                        this.unSubscribeSymbol({ market_id: MarketIDMap[market_id], security_id: security_id });
                        subs.unsubscribe();
                        this._quoteStore.quotes.set(this._quoteStore.getMapKey(MarketIDMap[market_id], security_id), quoteObj)
                        clearTimeout(timer);
                        resolve(Object.assign({}, quoteObj))
                    }
                });
            }
        });
    }

    updateQuotesMap(res: LTPAndClosePiceObj[]) {
        let quotesObj = [];
        res?.forEach((symbol) => {
            const existingQuote = this._quoteStore.quotes.get(this._quoteStore.getMapKey(MarketIDMap[symbol.ExchangeId], symbol.SecurityId));
            if (!existingQuote || existingQuote.LTP <= 0) {
                let quoteObj = new Quote();
                quoteObj.marketId = symbol.ExchangeId;
                quoteObj.securityId = symbol.SecurityId;
                quoteObj.LTP = symbol.LastTradePrice;
                quoteObj.closePrice = symbol.ClosePrice;
                quoteObj.prevClosePrice = symbol.PrevClose

                quotesObj.push(quoteObj);
                this._quoteStore.quotes.set(this._quoteStore.getMapKey(MarketIDMap[symbol.ExchangeId], symbol.SecurityId), quoteObj)
            } else if (existingQuote && existingQuote.LTP >= 0) {
                quotesObj.push(existingQuote);
            }
        })
        return quotesObj;
    }

    fetchClosePrice(symbols: any[]) {
        let data: LTPAndClosePiceObj[] = [];
        symbols.forEach(el => {
            const struct: LTPAndClosePiceObj = {
                ExchangeId: el[0],
                SecurityId: el[1],
                ClosePrice: 0,
                LastTradePrice: 0,
                PrevClose: 0,
                isLtp: false
            }
            if (this.symbolSearchService.indicesClosePriceMap.has(Utils.getJoinedString(el[0], el[1]))) {
                struct.ClosePrice = this.symbolSearchService.indicesClosePriceMap.get(Utils.getJoinedString(el[0], el[1]))[0]
                struct.PrevClose = this.symbolSearchService.indicesClosePriceMap.get(Utils.getJoinedString(el[0], el[1]))[1]
            }
            else if (this.symbolSearchService.symbolInfoMap.has(Utils.getJoinedString(MarketIDMap[el[0]], el[1]))) {
                /**
                 * in symbolinfo map against a key we have array has value, in which
                 * index 0 is market lot
                 * index 1 is tick size
                 * index 2 is scrip precision
                 * index 3 is close price
                 * index 4 is previous close price
                 */
                struct.ClosePrice = this.symbolSearchService.symbolInfoMap.get(Utils.getJoinedString(MarketIDMap[el[0]], el[1]))[3]
                struct.PrevClose = this.symbolSearchService.symbolInfoMap.get(Utils.getJoinedString(MarketIDMap[el[0]], el[1]))[4]
            }
            struct.LastTradePrice = struct.ClosePrice;

            data.push(struct)
        })

        return data;
    }

    /**
     * @param key combination of symbolObject.
     * @returns actual price for the price entered by user
     */
    strikePricesObj(key: string) {
        if (this.strikePricesMap.has(key)) {
            return this.strikePricesMap.get(key);
        }
        return null;
    }

    sendDeltaRequest(underlyingExchangeId: number, underlyingSecurityId: number, optionExchangeId: number, optionSecurityId: number) {
        const commandCategory = Utils.htonl(Request.REQUEST_DELTA);

        const req = new DeltaRequest();
        req.setUnderlyingExchangeId(underlyingExchangeId);
        req.setUnderlyingSecurityId(underlyingSecurityId);
        req.setOptionExchangeId(optionExchangeId);
        req.setOptionSecurityId(optionSecurityId);


        // Converting to typed array
        const deltaProto = req.serializeBinary();

        // Creating Command Category
        const commandCategoryBuff = new Uint8Array(commandCategory);
        const deltaReq = new Uint8Array(commandCategoryBuff.byteLength + commandCategoryBuff.byteLength + deltaProto.length);

        /** Setting data for binary request
         0-4 Total Message Length
         4-8 Message type
         8-* Message body
         */
        deltaReq.set(new Uint8Array(Utils.htonl(commandCategoryBuff.byteLength + deltaProto.length)), 0);
        deltaReq.set(commandCategoryBuff, commandCategoryBuff.byteLength);
        deltaReq.set(deltaProto, 8);

        if (this.connection$ && !this.connection$.closed && this.authenticatedSocket) {
            this.connection$.next(deltaReq);
        }
    }
}
